import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  CBadge,
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSmartTable,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilHistory, cilReload } from '@coreui/icons'

import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import UserService from 'src/service/UserService'
import {
  formatBuild,
  formatDeviceDetails,
  formatPlatform,
  formatUtcDateTime,
} from 'src/utils/appDevice'

const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'device_type', label: 'Device' },
  { key: 'id', label: 'Member ID' },
]

// Presence comes from the mobile app's app_events: a member is online while the app has logged
// an event for them in this game in the last 5 minutes. The listing is ordered by last activity
// server-side and ignores a sort param, so no column here offers a sorter it cannot honour.
const columns = [
  { label: 'Member ID', key: 'id', filter: false, sorter: false, _style: { width: '10%' } },
  { label: 'Member Name', key: 'full_name', filter: false, sorter: false },
  { label: 'Email', key: 'email', filter: false, sorter: false },
  { label: 'Phone', key: 'phone', filter: false, sorter: false },
  { label: 'Session Start', key: 'login_at', filter: false, sorter: false },
  { label: 'Last Activity', key: 'last_activity_at', filter: false, sorter: false },
  { label: 'Device', key: 'device_type', filter: false, sorter: false },
  { label: 'Status', key: 'online_status', filter: false, sorter: false },
  { label: 'History', key: 'history', filter: false, sorter: false, _style: { width: '1%' } },
]

const renderDevice = (item) => {
  const platform = formatPlatform(item.device_type)
  if (!platform) return <td className="um-muted">-</td>
  const build = formatBuild(item)
  const details = formatDeviceDetails(item)
  return (
    <td title={details || undefined}>
      <div className="fw-semibold">{platform}</div>
      {build && <div className="um-muted small">{build}</div>}
    </td>
  )
}

const LoginMember = ({ onTotalChange }) => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()
  // Presence is live data, so let the admin re-pull it without leaving the page.
  const [refreshKey, setRefreshKey] = useState(0)

  const [historyVisible, setHistoryVisible] = useState(false)
  const [historyMember, setHistoryMember] = useState(null)
  const [historyData, setHistoryData] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  // Guards against a slow response for a previously opened member landing in the modal.
  const historyRequestRef = useRef(0)

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })

    setLoading(true)
    UserService.loginUserList(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setUsers(result.data)
        onTotalChange?.(result.data?.total ?? 0)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
    // refreshKey is a manual re-fetch trigger, not a request parameter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, columnFilter, itemsPerPage, refreshKey, onTotalChange])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const openHistory = async (member) => {
    const requestId = ++historyRequestRef.current
    setHistoryMember(member)
    setHistoryVisible(true)
    setHistoryLoading(true)
    setHistoryData([])

    try {
      const res = await UserService.memberLoginHistory(member.id)
      if (requestId === historyRequestRef.current && res.success) {
        setHistoryData(res.data || [])
      }
    } catch {
      // The empty state already reads "No sessions found"; nothing else to surface.
    } finally {
      if (requestId === historyRequestRef.current) setHistoryLoading(false)
    }
  }

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label
  const onlineCount = users?.total ?? 0

  return (
    <>
      <div className="um-panel-heading">
        <div>
          <h2 className="um-panel-title">Online Members</h2>
          <p className="um-panel-note">
            {onlineCount} {onlineCount === 1 ? 'member' : 'members'} active in the last 5 minutes.
          </p>
        </div>
      </div>

      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={handleFieldChange}
        value={columnFilter?.[searchField] || ''}
        onSearch={applySearch}
        placeholder={`Search by ${activeSearchLabel}...`}
        trailing={
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            onClick={() => setRefreshKey((current) => current + 1)}
            title="Refresh online members"
          >
            <CIcon icon={cilReload} className="me-1" />
            Refresh
          </CButton>
        }
      />

      <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
        <CSmartTable
          columns={columns}
          noItemsLabel="No members are online right now"
          scopedColumns={{
            id: (item) => <td className="um-muted">{item.id}</td>,
            full_name: (item) => <td className="fw-semibold">{item.full_name}</td>,
            phone: (item) => <td>{item.phone || '-'}</td>,
            login_at: (item) => <td>{formatUtcDateTime(item.login_at)}</td>,
            last_activity_at: (item) => <td>{formatUtcDateTime(item.last_activity_at)}</td>,
            device_type: renderDevice,
            online_status: () => (
              <td>
                <span className="um-online-status">
                  <span className="um-online-dot" />
                  Online
                </span>
              </td>
            ),
            history: (item) => (
              <td>
                <CButton
                  size="sm"
                  color="primary"
                  variant="outline"
                  onClick={() => openHistory(item)}
                >
                  <CIcon icon={cilHistory} className="me-1" />
                  History
                </CButton>
              </td>
            ),
          }}
          items={users?.data}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          pagination={{ external: true }}
          paginationProps={{
            activePage,
            pages: Math.ceil(users?.total / itemsPerPage) || 1,
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onActivePageChange={(page) => setActivePage(page)}
          onItemsPerPageChange={(perPage) => {
            setActivePage(1)
            setItemsPerPage(perPage)
          }}
        />
      </div>

      <CModal
        visible={historyVisible}
        size="xl"
        scrollable
        onClose={() => setHistoryVisible(false)}
      >
        <CModalHeader>
          <div>
            <CModalTitle>App Sessions</CModalTitle>
            {historyMember && <p className="um-history-subtitle">{historyMember.full_name}</p>}
          </div>
        </CModalHeader>

        <CModalBody>
          {historyLoading ? (
            <div className="text-center py-4">
              <CSpinner color="primary" />
            </div>
          ) : (
            <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
              <CTable responsive hover className="align-middle mb-0">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Device</CTableHeaderCell>
                    <CTableHeaderCell>Session Start</CTableHeaderCell>
                    <CTableHeaderCell>Last Activity</CTableHeaderCell>
                    <CTableHeaderCell>Screen Views</CTableHeaderCell>
                    <CTableHeaderCell>Status</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {historyData.length > 0 ? (
                    historyData.map((row) => (
                      <CTableRow key={row.session_id}>
                        {renderDevice(row)}
                        <CTableDataCell>
                          {formatUtcDateTime(row.login_at, 'DD.MM.YYYY HH:mm:ss')}
                        </CTableDataCell>
                        <CTableDataCell>
                          {formatUtcDateTime(row.last_activity_at, 'DD.MM.YYYY HH:mm:ss')}
                        </CTableDataCell>
                        <CTableDataCell>{row.screen_views ?? 0}</CTableDataCell>
                        <CTableDataCell>
                          {row.is_active ? (
                            <CBadge color="success" shape="rounded-pill" className="px-2 py-1">
                              Active Session
                            </CBadge>
                          ) : (
                            <CBadge color="secondary" shape="rounded-pill" className="px-2 py-1">
                              Ended
                            </CBadge>
                          )}
                        </CTableDataCell>
                      </CTableRow>
                    ))
                  ) : (
                    <CTableRow>
                      <CTableDataCell colSpan={5} className="text-center um-muted py-4">
                        No sessions found
                      </CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>
            </div>
          )}
        </CModalBody>

        <CModalFooter>
          <CButton color="secondary" onClick={() => setHistoryVisible(false)}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default LoginMember
